import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// utils and helpers
import { searchFilterTransactions } from "../utils/searchFilterTransactions";
import { StatusBadge } from "@/utils/resolveStatusStyle";
import { TRANSACTION_TYPE, TRANSACTION_STATUS } from "@/utils/statusStyles";
import { formatDateTime } from "@/utils/formatDateTime";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchTransactionsList } from "@/services/transactionsApi";
import { useBusiness } from "@hooks/useBusiness";
import { useStorePath } from "@hooks/useStorePath";

export default function useTransactionsTable() {
  const navigate = useNavigate();
  const { storePath } = useStorePath();
  const [searchParams, setSearchParams] = useSearchParams();
  const { businessId, users, currency } = useBusiness();

  const searchTerm = searchParams.get("search") || "";
  const typeFilter = searchParams.get("type") || "All";
  const statusFilter = searchParams.get("status") || "All";

  const updateParam = useCallback(
    (key, val, emptyValue = "All") => {
      const params = new URLSearchParams(searchParams);
      if (val && val !== emptyValue) params.set(key, val);
      else params.delete(key);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchTerm = useCallback((val) => updateParam("search", val, ""), [updateParam]);
  const updateTypeFilter = useCallback((val) => updateParam("type", val), [updateParam]);
  const updateStatusFilter = useCallback((val) => updateParam("status", val), [updateParam]);

  const {
    data: transactions = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // Keyed by business so a different account never reads a stale cache. Staff
    // is in the key too, since it decides the cashier name on every row.
    queryKey: ["transactions", businessId, Object.keys(users).length],
    queryFn: () => fetchTransactionsList(businessId, users),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const filteredTransactions = useMemo(
    () => searchFilterTransactions(transactions, searchTerm, typeFilter, statusFilter),
    [transactions, searchTerm, typeFilter, statusFilter]
  );

  const handleView = useCallback(
    (txn) => {
      if (!txn?.id) return;
      navigate(storePath(`transactions/${txn.id}`));
    },
    [navigate]
  );

  const transactionColumns = useMemo(
    () => [
      {
        accessorKey: "transactionNumber",
        header: "Transaction No.",
        cell: ({ row }) => row.original.transactionNumber || "-",
      },
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => formatDateTime(row.original.date),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => <StatusBadge map={TRANSACTION_TYPE} status={row.original.type} />,
      },
      {
        accessorKey: "customer.name",
        header: "Customer",
        cell: ({ row }) =>
          row.original.customer?.name || <span className="text-gray-400 italic">Walk-in</span>,
      },
      {
        accessorKey: "cashier",
        header: "Cashier",
        cell: ({ row }) => row.original.cashier || "-",
      },
      {
        accessorKey: "itemsCount",
        header: "Items",
        cell: ({ row }) => row.original.itemsCount ?? 0,
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ row }) => {
          const total = row.original.total;
          const color = total < 0 ? "text-red-600" : "text-[#1a1a1a]";
          return (
            <span className={`font-semibold ${color}`}>
              {formatCurrency(total, row.original.currency || currency)}
            </span>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge map={TRANSACTION_STATUS} status={row.original.status} />,
      },
    ],
    [currency]
  );

  const renderTransactionRow = useCallback(
    (row) => {
      const txn = row.original;
      return (
        <TableRow
          key={row.id}
          onClick={() => handleView(txn)}
          className="cursor-pointer hover:bg-[#f6f6ff] transition-colors"
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
              {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
            </TableCell>
          ))}
        </TableRow>
      );
    },
    [handleView]
  );

  return {
    allTransactions: transactions,
    transactions: filteredTransactions,
    searchTerm,
    updateSearchTerm,
    typeFilter,
    updateTypeFilter,
    statusFilter,
    updateStatusFilter,
    refetch,
    transactionColumns,
    renderTransactionRow,
    isLoading,
    isError,
  };
}
